import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuthV2 } from '../../contexts/AuthContextV2';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useFlightMetadataOptions } from '../../hooks/useFlightMetadataOptions';
import { useTranslation } from '../../i18n';
import { isAdmin as checkIsAdmin } from '../../config/roles';
import type { Customer, FlightType, OrgMember, Payload, Pilot, Project } from '../../types/api';
import type { FileInfo } from './FileUploader.styled';
import { keepFailedSelections, type UploadOutcome } from './uploadResults';

type DropzoneState = ReturnType<typeof useDropzone>;

interface UploadMetadata {
  destinationPath: string;
  flightType: string;
  payloadId: number;
  pilotId: string;
  customerId: string;
  projectId: string;
  airspaceObserverId: string;
  payloadOperatorId: string;
  bypassValidation?: boolean;
}

interface UseFileUploaderParams {
  onUpload: (files: File[], metadata: UploadMetadata) => Promise<UploadOutcome | void>;
  onAnalyze: (file: File, flightType?: string) => Promise<void>;
  loading: boolean;
  allowedTypes: string[];
  maxFiles: number;
  currentPath: string;
}

export interface UseFileUploaderResult {
  // Status
  error: string;
  success: string;
  uploadProgress: number;
  selectedFiles: FileInfo[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileInfo[]>>;
  isXerAdmin: boolean;
  // Dropzone
  getRootProps: DropzoneState['getRootProps'];
  getInputProps: DropzoneState['getInputProps'];
  isDragActive: boolean;
  removeFile: (index: number) => void;
  // Metadata dialog open state
  showMetadataDialog: boolean;
  setShowMetadataDialog: React.Dispatch<React.SetStateAction<boolean>>;
  // Metadata option lists
  flightTypes: FlightType[];
  payloads: Payload[];
  pilots: Pilot[];
  customers: Customer[];
  projects: Project[];
  airspaceObservers: OrgMember[];
  payloadOperators: OrgMember[];
  loadingOptions: boolean;
  // Metadata field values + setters
  flightType: string;
  setFlightType: React.Dispatch<React.SetStateAction<string>>;
  payload: number | '';
  setPayload: React.Dispatch<React.SetStateAction<number | ''>>;
  pilot: string | '';
  setPilot: React.Dispatch<React.SetStateAction<string | ''>>;
  customer: string | '';
  setCustomer: React.Dispatch<React.SetStateAction<string | ''>>;
  project: string | '';
  setProject: React.Dispatch<React.SetStateAction<string | ''>>;
  airspaceObserver: string | '';
  setAirspaceObserver: React.Dispatch<React.SetStateAction<string | ''>>;
  payloadOperator: string | '';
  setPayloadOperator: React.Dispatch<React.SetStateAction<string | ''>>;
  isTestMode: boolean;
  setIsTestMode: React.Dispatch<React.SetStateAction<boolean>>;
  // Add-option dialog open state
  showAddCustomerDialog: boolean;
  setShowAddCustomerDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showAddProjectDialog: boolean;
  setShowAddProjectDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showAddPayloadDialog: boolean;
  setShowAddPayloadDialog: React.Dispatch<React.SetStateAction<boolean>>;
  // Add-option field values + setters
  newCustomerName: string;
  setNewCustomerName: React.Dispatch<React.SetStateAction<string>>;
  newProjectName: string;
  setNewProjectName: React.Dispatch<React.SetStateAction<string>>;
  newPayloadName: string;
  setNewPayloadName: React.Dispatch<React.SetStateAction<string>>;
  newPayloadWeight: string;
  setNewPayloadWeight: React.Dispatch<React.SetStateAction<string>>;
  addingCustomer: boolean;
  addingProject: boolean;
  addingPayload: boolean;
  // Handlers
  handleAnalyze: (file: File) => Promise<void>;
  handleUpload: () => Promise<void>;
  handleAddCustomer: () => Promise<void>;
  handleAddProject: () => Promise<void>;
  handleAddPayload: () => Promise<void>;
}

export const useFileUploader = ({
  onUpload,
  onAnalyze,
  loading,
  allowedTypes,
  maxFiles,
  currentPath,
}: UseFileUploaderParams): UseFileUploaderResult => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<FileInfo[]>([]);
  const [uploadProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { showError, showSuccess } = useErrorHandler();
  const [showMetadataDialog, setShowMetadataDialog] = useState<boolean>(false);
  const [flightType, setFlightType] = useState<string>('');
  const [payload, setPayload] = useState<number | ''>('');
  const [pilot, setPilot] = useState<string | ''>('');
  const [customer, setCustomer] = useState<string | ''>('');
  const [project, setProject] = useState<string | ''>('');
  const [airspaceObserver, setAirspaceObserver] = useState<string | ''>('');
  const [payloadOperator, setPayloadOperator] = useState<string | ''>('');
  const [isTestMode, setIsTestMode] = useState<boolean>(false);

  // Use shared hook for flight metadata options
  const {
    flightTypes,
    payloads,
    pilots,
    customers,
    projects,
    airspaceObservers,
    payloadOperators,
    loading: loadingOptions,
    error: optionsError,
    addCustomer: addCustomerToOptions,
    addProject: addProjectToOptions,
    addPayload: addPayloadToOptions,
  } = useFlightMetadataOptions({ enabled: showMetadataDialog });

  // Show error if metadata options fail to load (only once per error)
  const handledErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (optionsError && handledErrorRef.current !== optionsError) {
      handledErrorRef.current = optionsError;
      const errorMsg = t('fileManagement.failedToLoadData');
      setError(errorMsg);
      showError(errorMsg);
    }
  }, [optionsError, showError, t]);

  // State for add customer/project/payload dialogs
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState<boolean>(false);
  const [showAddProjectDialog, setShowAddProjectDialog] = useState<boolean>(false);
  const [showAddPayloadDialog, setShowAddPayloadDialog] = useState<boolean>(false);
  const [newCustomerName, setNewCustomerName] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [newPayloadName, setNewPayloadName] = useState<string>('');
  const [newPayloadWeight, setNewPayloadWeight] = useState<string>('');
  const [addingCustomer, setAddingCustomer] = useState<boolean>(false);
  const [addingProject, setAddingProject] = useState<boolean>(false);
  const [addingPayload, setAddingPayload] = useState<boolean>(false);

  // Get user profile to check if xer_admin
  const { profile } = useAuthV2();
  const isXerAdmin =
    (profile?.organization?.slug === 'xer-technologies' &&
      checkIsAdmin(profile?.organization?.roles)) ||
    false;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError('');

      // Check file count
      if (acceptedFiles.length + selectedFiles.length > maxFiles) {
        setError(t('fileManagement.maxFilesAllowed', { count: maxFiles }));
        return;
      }

      // Check file types
      const invalidFiles = acceptedFiles.filter((file) => {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        return !allowedTypes.includes(extension);
      });

      if (invalidFiles.length > 0) {
        setError(t('fileManagement.invalidFileTypes', { types: allowedTypes.join(', ') }));
        return;
      }

      // Add files to selection
      const newFiles: FileInfo[] = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setSelectedFiles((prev) => [...prev, ...newFiles]);
    },
    [selectedFiles, allowedTypes, maxFiles, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: allowedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxFiles,
    disabled: loading,
  });

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleAnalyze = async (file: File) => {
    try {
      setError('');
      await onAnalyze(file, flightType || undefined);
      const successMsg = t('fileManagement.analysisCompleted', { fileName: file.name });
      setSuccess(successMsg);
      showSuccess(successMsg);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('fileManagement.failedToAnalyze');
      setError(errorMsg);
      showError(err, t('fileManagement.failedToAnalyze'));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      const errorMsg = t('fileManagement.selectFilesToUpload');
      setError(errorMsg);
      showError(errorMsg);
      return;
    }

    // Check if any files are CSV files (which require flight type and payload)
    const hasCsvFiles = selectedFiles.some((f) => f.file.name.toLowerCase().endsWith('.csv'));

    // Skip validation if in test mode and user is xer_admin
    if (!isTestMode) {
      if (hasCsvFiles && !flightType) {
        const errorMsg = t('fileManagement.selectFlightTypeForCsv');
        setError(errorMsg);
        showError(errorMsg);
        return;
      }

      if (hasCsvFiles && !payload) {
        const errorMsg = t('fileManagement.selectPayloadForCsv');
        setError(errorMsg);
        showError(errorMsg);
        return;
      }

      if (hasCsvFiles && !pilot && flightType !== 'BI' && flightType !== 'RT') {
        const errorMsg = t('fileManagement.selectPilotForCsv');
        setError(errorMsg);
        showError(errorMsg);
        return;
      }
    }

    try {
      setError('');

      const files = selectedFiles.map((f) => f.file);

      const outcome = await onUpload(files, {
        destinationPath: currentPath,
        flightType: hasCsvFiles ? flightType : '',
        payloadId: hasCsvFiles ? (payload as number) : 0,
        pilotId:
          hasCsvFiles && flightType !== 'BI' && flightType !== 'RT' && pilot !== 'other'
            ? (pilot as string)
            : '',
        customerId: hasCsvFiles && customer ? (customer as string) : '',
        projectId: hasCsvFiles && project ? (project as string) : '',
        airspaceObserverId: hasCsvFiles && airspaceObserver ? (airspaceObserver as string) : '',
        payloadOperatorId: hasCsvFiles && payloadOperator ? (payloadOperator as string) : '',
        bypassValidation: isTestMode,
      });

      // Keep only the files that genuinely failed, so a part-succeeded batch
      // doesn't re-offer files that are already stored. A caller that reports
      // no outcome is treated as fully handled (#1172).
      const failedIndices = (outcome as UploadOutcome | undefined)?.failedIndices ?? [];
      setSelectedFiles((previous) => keepFailedSelections(previous, failedIndices));

      if (failedIndices.length === 0) {
        setShowMetadataDialog(false);
        resetForm();
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('fileManagement.failedToUpload');
      setError(errorMsg);
      showError(err, t('fileManagement.failedToUpload'));
    }
  };

  const resetForm = () => {
    setFlightType('');
    setPayload('');
    setPilot('');
    setCustomer('');
    setProject('');
    setAirspaceObserver('');
    setPayloadOperator('');
    setIsTestMode(false);
  };

  const handleAddCustomer = async () => {
    if (!newCustomerName.trim()) return;
    try {
      setAddingCustomer(true);
      const newCustomer = await addCustomerToOptions(newCustomerName.trim());
      setCustomer(newCustomer.id);
      setNewCustomerName('');
      setShowAddCustomerDialog(false);
      showSuccess(t('fileManagement.customerAdded'));
    } catch (err) {
      showError(err, t('fileManagement.failedToAddCustomer'));
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleAddProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      setAddingProject(true);
      const newProject = await addProjectToOptions(newProjectName.trim());
      setProject(newProject.id);
      setNewProjectName('');
      setShowAddProjectDialog(false);
      showSuccess(t('fileManagement.projectAdded'));
    } catch (err) {
      showError(err, t('fileManagement.failedToAddProject'));
    } finally {
      setAddingProject(false);
    }
  };

  const handleAddPayload = async () => {
    if (!newPayloadName.trim()) return;
    try {
      setAddingPayload(true);
      const weight = newPayloadWeight ? parseFloat(newPayloadWeight) : undefined;
      const newPayloadItem = await addPayloadToOptions(newPayloadName.trim(), weight);
      setPayload(newPayloadItem.id);
      setNewPayloadName('');
      setNewPayloadWeight('');
      setShowAddPayloadDialog(false);
      showSuccess(t('fileManagement.payloadAdded'));
    } catch (err) {
      showError(err, t('fileManagement.failedToAddPayload'));
    } finally {
      setAddingPayload(false);
    }
  };

  return {
    error,
    success,
    uploadProgress,
    selectedFiles,
    setSelectedFiles,
    isXerAdmin,
    getRootProps,
    getInputProps,
    isDragActive,
    removeFile,
    showMetadataDialog,
    setShowMetadataDialog,
    flightTypes,
    payloads,
    pilots,
    customers,
    projects,
    airspaceObservers,
    payloadOperators,
    loadingOptions,
    flightType,
    setFlightType,
    payload,
    setPayload,
    pilot,
    setPilot,
    customer,
    setCustomer,
    project,
    setProject,
    airspaceObserver,
    setAirspaceObserver,
    payloadOperator,
    setPayloadOperator,
    isTestMode,
    setIsTestMode,
    showAddCustomerDialog,
    setShowAddCustomerDialog,
    showAddProjectDialog,
    setShowAddProjectDialog,
    showAddPayloadDialog,
    setShowAddPayloadDialog,
    newCustomerName,
    setNewCustomerName,
    newProjectName,
    setNewProjectName,
    newPayloadName,
    setNewPayloadName,
    newPayloadWeight,
    setNewPayloadWeight,
    addingCustomer,
    addingProject,
    addingPayload,
    handleAnalyze,
    handleUpload,
    handleAddCustomer,
    handleAddProject,
    handleAddPayload,
  };
};
